import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import emailjs from "@emailjs/browser";

/* ─────────────────────────────────────────────────────────────────────────────
   INSTALLATION REQUISE (à lancer UNE FOIS dans le dossier du projet) :
   npm install framer-motion gsap @gsap/react lenis
   ───────────────────────────────────────────────────────────────────────────── */

// ─── LAZY IMPORTS GSAP / LENIS ───────────────────────────────────────────────
// On importe via dynamic import pour ne pas casser si les libs ne sont pas là
let gsapLib = null, ScrollTrigger = null;
const loadLibs = async () => {
  try {
    const [g, l] = await Promise.all([
      import("gsap").catch(() => null),
      import("lenis").catch(() => null),
    ]);
    if (g) {
      const st = await import("gsap/ScrollTrigger").catch(() => null);
      gsapLib = g.gsap;
      if (st) { ScrollTrigger = st.ScrollTrigger; gsapLib.registerPlugin(ScrollTrigger); }
    }
    if (l) { /* lenis chargé mais géré par l'app */ }
  } catch (_) {}
};

// ─── THEMES ──────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg:         "#07050F",
    bgCard:     "rgba(255,255,255,0.018)",
    border:     "rgba(139,92,246,0.16)",
    primary:    "#9D5CF6",
    secondary:  "#7C3AED",
    tertiary:   "#A78BFA",
    warm3:      "#C4B5FD",
    warm4:      "#8B5CF6",
    warm5:      "#6D28D9",
    text:       "#F0EBF8",
    muted:      "rgba(240,235,248,0.46)",
    faint:      "rgba(240,235,248,0.20)",
    navBg:      "rgba(7,5,15,0.90)",
    cardShadow: "0 0 0 1px rgba(139,92,246,0.10)",
    gridColor:  "rgba(139,92,246,0.035)",
    orbColors:  ["#4C1D95","#6D28D9","#7C3AED"],
    trackBg:    "rgba(240,235,248,0.07)",
    inputBg:    "rgba(255,255,255,0.018)",
    cursorColor:"#9D5CF6",
    glowOrb1:   "rgba(109,40,217,0.22)",
    glowOrb2:   "rgba(157,92,246,0.16)",
    glowOrb3:   "rgba(76,29,149,0.18)",
  },
  light: {
    bg:         "#FAFAFF",
    bgCard:     "rgba(109,40,217,0.022)",
    border:     "rgba(109,40,217,0.13)",
    primary:    "#7C3AED",
    secondary:  "#6D28D9",
    tertiary:   "#8B5CF6",
    warm3:      "#9D5CF6",
    warm4:      "#7C3AED",
    warm5:      "#5B21B6",
    text:       "#160D22",
    muted:      "rgba(22,13,34,0.52)",
    faint:      "rgba(22,13,34,0.26)",
    navBg:      "rgba(250,250,255,0.92)",
    cardShadow: "0 0 0 1px rgba(109,40,217,0.08), 0 2px 20px rgba(109,40,217,0.04)",
    gridColor:  "rgba(109,40,217,0.05)",
    orbColors:  ["#DDD6FE","#C4B5FD","#EDE9FE"],
    trackBg:    "rgba(22,13,34,0.07)",
    inputBg:    "rgba(109,40,217,0.022)",
    cursorColor:"#7C3AED",
    glowOrb1:   "rgba(109,40,217,0.08)",
    glowOrb2:   "rgba(157,92,246,0.06)",
    glowOrb3:   "rgba(76,29,149,0.07)",
  },
};

const NAV_LINKS = ["About", "Projects", "Skills", "Contact"];

const ALL_PROJECTS = [
  { title:"Jeu RPG open world",        tag:"2023", desc:"Développement d'un RPG complet avec carte ouverte, gestion d'états et système de combats.", tech:["Python"],                     tKey:"primary"   },
  { title:"Application club hippique", tag:"2023", desc:"App mobile de gestion de club (membres, cours, chevaux) avec interface cross-platform.",   tech:["Flutter"],                    tKey:"secondary" },
  { title:"Jeu 2D — Unity / C#",       tag:"2023", desc:"Conception d'un jeu 2D avec animations, gestion physique et système de niveaux.",           tech:["Unity","C#"],                 tKey:"tertiary"  },
  { title:"Memory en ligne",           tag:"2022", desc:"Jeu de mémoire multijoueur en ligne avec score persistant et interface dynamique.",          tech:["HTML","PHP","SQL","JS"],      tKey:"warm3"     },
  { title:"Réseau social local",       tag:"2026", desc:"Application web permettant le partage d'informations sous forme de posts en étant connecté avec son compte.", tech:["Laravel","React","MySQL"], tKey:"warm5" },
  { title:"Gestionnaire de cinéma",    tag:"2026", desc:"Espace dédié à un gestionnaire de cinéma lui permettant de programmer des séances et consulter films/salles.", tech:["HTML","PHP","SQL","JS"],  tKey:"warm4" },
  { title:"Portfolio",                 tag:"2026", desc:"Création d'un portfolio en Jekyll afin de mieux maîtriser les bases du fonctionnement du markdown.", tech:["Jekyll","Ruby","Markdown"], tKey:"primary" },
  { title:"Site commercial",           tag:"2026", desc:"Reproduction d'un site fictif statique afin de se familiariser avec Tailwind, un framework CSS.", tech:["HTML","Tailwind"],         tKey:"secondary" },
];

const SKILLS_DATA = [
  { name:"React",      tKey:"primary"   },
  { name:"Java",       tKey:"secondary" },
  { name:"Javascript", tKey:"tertiary"  },
  { name:"PHP",        tKey:"warm3"     },
  { name:"Docker",     tKey:"warm4"     },
  { name:"HTML / CSS", tKey:"warm5"     },
  { name:"MySQL",      tKey:"primary"   },
  { name:"Tailwind",   tKey:"secondary" },
];

const SKILL_TO_TECH = {
  "React":      ["React"],
  "Java":       ["Java"],
  "Javascript": ["JS"],
  "PHP":        ["PHP"],
  "Docker":     ["Docker"],
  "HTML / CSS": ["HTML","CSS"],
  "MySQL":      ["SQL","MySQL"],
  "Tailwind":   ["Tailwind"],
};

const SKILL_GROUPS = [
  { label:"Frontend",       skills:["React","Javascript","HTML / CSS","Tailwind"] },
  { label:"Backend & Data", skills:["Java","PHP","MySQL","Docker"] },
];

const TECH_ICONS = {
  "React": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <ellipse cx="14" cy="14" rx="12" ry="4.5" stroke={c} strokeWidth="1.3"/>
      <ellipse cx="14" cy="14" rx="12" ry="4.5" stroke={c} strokeWidth="1.3" transform="rotate(60 14 14)"/>
      <ellipse cx="14" cy="14" rx="12" ry="4.5" stroke={c} strokeWidth="1.3" transform="rotate(120 14 14)"/>
      <circle cx="14" cy="14" r="2.2" fill={c}/>
    </svg>
  ),
  "Java": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <path d="M9 9h10l-1.5 12H10.5L9 9z" stroke={c} strokeWidth="1.3" strokeLinejoin="round"/>
      <path d="M21 12h2.5a2.5 2.5 0 0 1 0 5H21" stroke={c} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M11.5 5.5c0 0 1.5-1.5 0-3" stroke={c} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M14.5 5.5c0 0 1.5-1.5 0-3" stroke={c} strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  ),
  "Javascript": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <rect x="3" y="3" width="22" height="22" rx="3.5" stroke={c} strokeWidth="1.3"/>
      <path d="M10 18.5c0 2 2.5 2.5 2.5 0V11" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M15.5 18c.5 1 1.5 1.5 2.5 1.5s2-.7 2-1.8c0-1.2-1-1.7-2.5-2.2-1-.4-2-1-2-2.2 0-1 .8-2 2.5-2 1 0 1.8.5 2.3 1.3" stroke={c} strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  ),
  "PHP": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <ellipse cx="14" cy="14" rx="12" ry="7" stroke={c} strokeWidth="1.3"/>
      <text x="14" y="18" textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="9" fontWeight="700" fill={c}>PHP</text>
    </svg>
  ),
  "Docker": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <rect x="2" y="11" width="6" height="5.5" rx="1" stroke={c} strokeWidth="1.3"/>
      <rect x="9.5" y="11" width="6" height="5.5" rx="1" stroke={c} strokeWidth="1.3"/>
      <rect x="2" y="4.5" width="6" height="5.5" rx="1" stroke={c} strokeWidth="1.3"/>
      <rect x="9.5" y="4.5" width="6" height="5.5" rx="1" stroke={c} strokeWidth="1.3"/>
      <rect x="17" y="4.5" width="6" height="5.5" rx="1" stroke={c} strokeWidth="1.3"/>
      <path d="M1 20.5 q1 5 5 5 h16 q4 0 5-5z" stroke={c} strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
      <circle cx="24.5" cy="16" r="1" fill={c}/>
      <path d="M24.5 16 c3.5 0 3.5-5 0-5" stroke={c} strokeWidth="1.3" strokeLinecap="round" fill="none"/>
    </svg>
  ),
  "HTML / CSS": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <path d="M4 3l2.5 20L14 25l7.5-2L24 3z" stroke={c} strokeWidth="1.3" strokeLinejoin="round"/>
      <path d="M9 9h10M9.5 14h9l-.7 5.5L14 20.5l-3.8-1-.2-2" stroke={c} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  "MySQL": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <ellipse cx="14" cy="7" rx="10" ry="3.5" stroke={c} strokeWidth="1.3"/>
      <path d="M4 7v7M24 7v7" stroke={c} strokeWidth="1.3"/>
      <ellipse cx="14" cy="14" rx="10" ry="3.5" stroke={c} strokeWidth="1.3"/>
      <path d="M4 14v7M24 14v7" stroke={c} strokeWidth="1.3"/>
      <ellipse cx="14" cy="21" rx="10" ry="3.5" stroke={c} strokeWidth="1.3"/>
    </svg>
  ),
  "Tailwind": (c) => (
    <svg viewBox="0 0 28 28" width="26" height="26" fill="none">
      <path d="M3 10 C7 3,11 3,14 10 C17 17,21 17,25 10" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M3 18 C7 11,11 11,14 18 C17 25,21 25,25 18" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

const EMAILJS_SERVICE_ID  = "service_6n51w5t";
const EMAILJS_TEMPLATE_ID = "template_m06ribk";
const EMAILJS_PUBLIC_KEY  = "d1OFpvzlCZ6RKd7Zn";

// ─── HOOKS ───────────────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [v, setV] = useState(() => typeof window !== "undefined" ? window.innerWidth < bp : false);
  useEffect(() => {
    const fn = () => setV(window.innerWidth < bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return v;
}

function useReducedMotion() {
  const [v, setV] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fn = (e) => setV(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return v;
}

// Smooth scroll flag
let scrollingProgrammatically = false;
let scrollTimeout = null;
function smoothScrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  scrollingProgrammatically = true;
  clearTimeout(scrollTimeout);
  el.scrollIntoView({ behavior: "smooth" });
  scrollTimeout = setTimeout(() => { scrollingProgrammatically = false; }, 900);
}

// ─── GLOBAL STYLES ───────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  ::selection { background: rgba(157,92,246,0.35); color: #F0EBF8; }

  /* Curseur custom — caché sur mobile */
  @media (pointer: fine) {
    body { cursor: none !important; }
    a, button, [role="button"] { cursor: none !important; }
  }

  @keyframes heroFadeUp   { from { opacity:0; transform:translateY(28px);  } to { opacity:1; transform:translateY(0); } }
  @keyframes heroFadeIn   { from { opacity:0; }                               to { opacity:1; } }
  @keyframes heroLineIn   { from { transform:scaleX(0); }                     to { transform:scaleX(1); } }
  @keyframes carouselProgress { from { width:0% } to { width:100% } }
  @keyframes highlightPulse { 0%,100%{opacity:1} 30%{opacity:0.7} 60%{opacity:1} }
  @keyframes spin         { to { transform:rotate(360deg); } }
  @keyframes orbFloat1    { 0%,100%{transform:translate(0,0) scale(1)}   50%{transform:translate(40px,-30px) scale(1.05)} }
  @keyframes orbFloat2    { 0%,100%{transform:translate(0,0) scale(1)}   50%{transform:translate(-30px,20px) scale(1.08)} }
  @keyframes orbFloat3    { 0%,100%{transform:translate(0,0) scale(1)}   50%{transform:translate(20px,40px)  scale(0.97)} }
  @keyframes cursorGrow   { to { transform:translate(-50%,-50%) scale(2.2); opacity:0.4; } }
  @keyframes glowPulse    { 0%,100%{box-shadow:0 0 0 0 currentColor} 50%{box-shadow:0 0 14px 2px currentColor} }
  @keyframes fadeSlideIn  { from{opacity:0;transform:translateX(-12px)} to{opacity:1;transform:translateX(0)} }
  @keyframes scaleIn      { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
  @keyframes bounce       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(6px)} }
  @keyframes marquee      { to { transform: translateX(-50%); } }
  @keyframes revealUp     { from { transform: translateY(115%); opacity:0; } to { transform: translateY(0); opacity:1; } }
  @keyframes lineWipe     { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0% 0 0); } }
  @keyframes gradShift    { 0%,100% { background-position:0% 50%; } 50% { background-position:100% 50%; } }
  @keyframes blobPulse    { 0%,100% { transform:translate(var(--bx),var(--by)) scale(1); } 50% { transform:translate(var(--bx),var(--by)) scale(1.08); } }

  .marquee-track { display:flex; width:max-content; animation:marquee 26s linear infinite; will-change:transform; }
  .marquee-track:hover { animation-play-state:paused; }

  .reveal-char { display:inline-block; overflow:hidden; }
  .reveal-char span { display:inline-block; }

  .card-3d { transition: transform 0.12s ease, box-shadow 0.3s ease; transform-style: preserve-3d; }
  .progress-bar { transform-origin: left; }

  /* Grain overlay */
  .grain::before {
    content:''; position:fixed; inset:-50%; pointer-events:none; z-index:9997;
    opacity:0.028; mix-blend-mode:overlay;
    background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size:200px 200px;
  }

  /* Scrollbar */
  ::-webkit-scrollbar { width:3px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(157,92,246,0.35); border-radius:2px; }
  ::-webkit-scrollbar-thumb:hover { background:rgba(157,92,246,0.7); }
`;

// ─── CUSTOM CURSOR ────────────────────────────────────────────────────────────
function CustomCursor({ color }) {
  const dot  = useRef(null);
  const ring = useRef(null);
  const blob = useRef(null);
  const pos     = useRef({ x: -200, y: -200 });
  const posBlob = useRef({ x: -200, y: -200 });
  const target  = useRef({ x: -200, y: -200 });
  const raf  = useRef(null);
  const hovering = useRef(false);

  useEffect(() => {
    const isFine = window.matchMedia("(pointer: fine)").matches;
    if (!isFine) return;

    const onMove = (e) => {
      target.current = { x: e.clientX, y: e.clientY };
      if (dot.current) dot.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    };

    const lerp = (a, b, t) => a + (b - a) * t;
    const tick = () => {
      pos.current.x     = lerp(pos.current.x,     target.current.x, 0.13);
      pos.current.y     = lerp(pos.current.y,     target.current.y, 0.13);
      posBlob.current.x = lerp(posBlob.current.x, target.current.x, 0.045);
      posBlob.current.y = lerp(posBlob.current.y, target.current.y, 0.045);
      if (ring.current) ring.current.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px)`;
      if (blob.current) blob.current.style.transform = `translate(${posBlob.current.x}px, ${posBlob.current.y}px)`;
      raf.current = requestAnimationFrame(tick);
    };

    const onEnter = (e) => {
      hovering.current = true;
      if (ring.current) {
        ring.current.style.width  = "48px";
        ring.current.style.height = "48px";
        ring.current.style.background = color + "22";
        ring.current.style.border = `1.5px solid ${color}88`;
        ring.current.style.opacity = "0.8";
      }
      if (blob.current) { blob.current.style.width = "280px"; blob.current.style.height = "280px"; blob.current.style.marginLeft = "-140px"; blob.current.style.marginTop = "-140px"; }
    };
    const onLeave = () => {
      hovering.current = false;
      if (ring.current) {
        ring.current.style.width  = "22px";
        ring.current.style.height = "22px";
        ring.current.style.background = "transparent";
        ring.current.style.border = `1.5px solid ${color}`;
        ring.current.style.opacity = "1";
      }
      if (blob.current) { blob.current.style.width = "200px"; blob.current.style.height = "200px"; blob.current.style.marginLeft = "-100px"; blob.current.style.marginTop = "-100px"; }
    };

    window.addEventListener("mousemove", onMove);
    document.querySelectorAll("a, button, [role=button]").forEach(el => {
      el.addEventListener("mouseenter", onEnter);
      el.addEventListener("mouseleave", onLeave);
    });
    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf.current);
    };
  }, [color]);

  const base = { position:"fixed", pointerEvents:"none", top:0, left:0, willChange:"transform" };

  return (
    <>
      {/* Blob — glow lent */}
      <div ref={blob} style={{
        ...base, zIndex:99996,
        width:200, height:200, borderRadius:"50%",
        background:`radial-gradient(circle, ${color}22 0%, transparent 70%)`,
        filter:"blur(28px)",
        marginLeft:-100, marginTop:-100,
        mixBlendMode:"screen",
        transition:"width 0.5s ease, height 0.5s ease, margin 0.5s ease",
      }} />
      {/* Dot */}
      <div ref={dot} style={{
        ...base, zIndex:99999,
        width:5, height:5, borderRadius:"50%",
        background:color,
        marginLeft:-2.5, marginTop:-2.5,
        boxShadow:`0 0 8px ${color}, 0 0 18px ${color}88`,
      }} />
      {/* Ring */}
      <div ref={ring} style={{
        ...base, zIndex:99998,
        width:22, height:22, borderRadius:"50%",
        border:`1.5px solid ${color}`,
        marginLeft:-11, marginTop:-11,
        transition:"width 0.3s cubic-bezier(.16,1,.3,1), height 0.3s cubic-bezier(.16,1,.3,1), background 0.3s ease, border 0.2s ease, opacity 0.3s ease",
        boxShadow:`0 0 10px ${color}44`,
      }} />
    </>
  );
}

// ─── GRADIENT ORB BACKGROUND ─────────────────────────────────────────────────
function GradientOrbs({ T }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:0, pointerEvents:"none", overflow:"hidden" }} aria-hidden>
      <div style={{
        position:"absolute", width:"75vw", height:"75vw", maxWidth:900, maxHeight:900,
        borderRadius:"50%", top:"-20%", left:"-15%",
        background:`radial-gradient(circle, ${T.glowOrb1} 0%, transparent 68%)`,
        animation:"orbFloat1 16s ease-in-out infinite",
        filter:"blur(8px)",
      }} />
      <div style={{
        position:"absolute", width:"60vw", height:"60vw", maxWidth:750, maxHeight:750,
        borderRadius:"50%", top:"25%", right:"-18%",
        background:`radial-gradient(circle, ${T.glowOrb2} 0%, transparent 68%)`,
        animation:"orbFloat2 20s ease-in-out infinite",
        filter:"blur(6px)",
      }} />
      <div style={{
        position:"absolute", width:"50vw", height:"50vw", maxWidth:620, maxHeight:620,
        borderRadius:"50%", bottom:"-14%", left:"25%",
        background:`radial-gradient(circle, ${T.glowOrb3} 0%, transparent 68%)`,
        animation:"orbFloat3 24s ease-in-out infinite",
        filter:"blur(10px)",
      }} />
    </div>
  );
}

// ─── SPLIT TEXT (char-by-char reveal) ────────────────────────────────────────
function SplitText({ text, delay = 0, stagger = 0.045, style = {}, charStyle = {} }) {
  return (
    <span style={style}>
      {text.split("").map((char, i) => (
        <span key={i} style={{
          display:"inline-block",
          overflow:"hidden",
          verticalAlign:"bottom",
          paddingTop:"0.18em",
          marginTop:"-0.18em",
          paddingBottom:"0.04em",
          marginBottom:"-0.04em",
        }}>
          <span style={{
            display:"inline-block",
            animation:`revealUp 0.9s ${(delay + i * stagger).toFixed(3)}s cubic-bezier(.16,1,.3,1) both`,
            ...charStyle,
          }}>
            {char === " " ? " " : char}
          </span>
        </span>
      ))}
    </span>
  );
}

// ─── ANIM COUNTER ─────────────────────────────────────────────────────────────
function AnimCounter({ target, size = 40, accent }) {
  const [display, setDisplay] = useState(target === "∞" ? "∞" : "0");
  const ref  = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    if (target === "∞") return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !done.current) {
        done.current = true;
        const hasPlus = target.includes("+");
        const num = parseInt(target);
        const dur = 1600;
        const start = performance.now();
        const tick = (now) => {
          const t = Math.min((now - start) / dur, 1);
          const eased = 1 - (1 - t) ** 3;
          const v = Math.round(eased * num);
          setDisplay(hasPlus ? `${v}+` : String(v));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
        obs.unobserve(el);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target]);

  return (
    <div ref={ref} style={{
      fontFamily:"'Bricolage Grotesque', sans-serif",
      fontSize:size, fontWeight:800, color:accent,
      lineHeight:1, marginBottom:6, letterSpacing:"-0.04em",
    }}>{display}</div>
  );
}

// ─── MAGNETIC WRAPPER ─────────────────────────────────────────────────────────
function MagneticWrapper({ children, strength = 0.3 }) {
  const ref = useRef(null);
  const [xy, setXy] = useState({ x:0, y:0 });
  const resting = xy.x === 0 && xy.y === 0;

  const onMove = useCallback((e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setXy({
      x: (e.clientX - (r.left + r.width  / 2)) * strength,
      y: (e.clientY - (r.top  + r.height / 2)) * strength,
    });
  }, [strength]);

  const onLeave = useCallback(() => setXy({ x:0, y:0 }), []);

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{
      display:"inline-block",
      transform:`translate(${xy.x}px, ${xy.y}px)`,
      transition: resting
        ? "transform 0.65s cubic-bezier(.16,1,.3,1)"
        : "transform 0.08s linear",
    }}>
      {children}
    </div>
  );
}

// ─── MARQUEE STRIP ────────────────────────────────────────────────────────────
function MarqueeStrip({ T, reversed = false, speed = 26 }) {
  const items = "DÉVELOPPEUR WEB  ·  REACT  ·  JAVASCRIPT  ·  FRONTEND  ·  UI / UX  ·  ALTERNANCE 2026  ·  DESIGN SYSTEM  ·  OPEN TO WORK  ·  ";
  return (
    <div style={{
      overflow:"hidden", position:"relative", zIndex:2,
      borderTop:`1px solid ${T.border}`, borderBottom:`1px solid ${T.border}`,
      padding:"11px 0",
    }}>
      <div className="marquee-track" style={{
        animationDuration:`${speed}s`,
        animationDirection: reversed ? "reverse" : "normal",
      }}>
        {[0,1].map(i => (
          <span key={i} style={{
            fontFamily:"'JetBrains Mono', monospace", fontSize:9,
            letterSpacing:3.5, textTransform:"uppercase", color:T.muted,
            paddingRight:"3em", whiteSpace:"nowrap", flexShrink:0,
          }}>
            {items}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── GLASS CARD ───────────────────────────────────────────────────────────────
function GlassCard({ T, children, style, onMouseEnter, onMouseLeave }) {
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={{
      background: T.bgCard, border:`1px solid ${T.border}`, borderRadius:14,
      boxShadow: T.cardShadow,
      transition:"background 0.4s, border 0.4s, box-shadow 0.4s",
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── THEME TOGGLE ─────────────────────────────────────────────────────────────
function ThemeToggle({ isDark, toggle, T }) {
  return (
    <button onClick={toggle} title={isDark ? "Mode clair" : "Mode sombre"} style={{
      width:44, height:24, borderRadius:12, border:`1px solid ${T.border}`,
      background: isDark ? "rgba(180,0,255,0.12)" : "rgba(140,0,180,0.08)",
      cursor:"pointer", position:"relative", flexShrink:0,
      transition:"all 0.3s ease", padding:0, outline:"none",
    }}>
      <span style={{ position:"absolute", left:5,  top:"50%", transform:"translateY(-50%)", fontSize:11, opacity:isDark?0.9:0.3, transition:"opacity 0.3s" }}>🌙</span>
      <span style={{ position:"absolute", right:5, top:"50%", transform:"translateY(-50%)", fontSize:11, opacity:isDark?0.3:0.9, transition:"opacity 0.3s" }}>☀️</span>
      <div style={{
        position:"absolute", top:3, width:18, height:18, borderRadius:"50%",
        background:T.primary, left: isDark ? 3 : "calc(100% - 21px)",
        transition:"left 0.3s cubic-bezier(.4,0,.2,1), background 0.3s",
        boxShadow:`0 1px 6px ${T.primary}88`,
      }} />
    </button>
  );
}

// ─── SECTION HEADER (animé au scroll) ────────────────────────────────────────
function SectionHeader({ T, overline, title, animRef, num }) {
  return (
    <div ref={animRef} style={{ marginBottom:48, opacity:0, transform:"translateY(24px)", transition:"opacity 0.7s ease, transform 0.7s ease", position:"relative", overflow:"hidden" }}>
      {num && (
        <div style={{
          position:"absolute", right:-8, top:"50%", transform:"translateY(-50%)",
          fontFamily:"'Bricolage Grotesque', sans-serif",
          fontSize:"clamp(72px,11vw,148px)", fontWeight:900,
          color:"transparent", WebkitTextStroke:`1px ${T.primary}14`,
          userSelect:"none", pointerEvents:"none", lineHeight:1,
          letterSpacing:"-0.06em",
        }}>{num}</div>
      )}
      <p style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:10, color:T.primary, letterSpacing:4, textTransform:"uppercase", margin:"0 0 14px", transition:"color 0.3s", opacity:0.7 }}>
        — {overline}
      </p>
      <h2 style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:"clamp(30px,5.5vw,62px)", fontWeight:800, color:T.text, margin:0, transition:"color 0.3s", letterSpacing:"-0.03em", lineHeight:0.94 }}>
        {title}
      </h2>
    </div>
  );
}

// ─── SCROLL REVEAL HOOK ───────────────────────────────────────────────────────
function useScrollReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = "1";
        el.style.transform = "translateY(0) translateX(0) scale(1)";
        obs.unobserve(el);
      }
    }, { threshold: 0.12, ...options });
    obs.observe(el);
    return () => obs.disconnect();
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps
  return ref;
}

// Reveal avec délai staggeré (utilisé sur les cards)
function useStaggerReveal(index = 0) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "translateY(22px) scale(0.97)";
    el.style.opacity = "0";
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          if (!el) return;
          el.style.transition = "opacity 0.7s cubic-bezier(.16,1,.3,1), transform 0.7s cubic-bezier(.16,1,.3,1)";
          el.style.opacity = "1";
          el.style.transform = "translateY(0) scale(1)";
          setTimeout(() => { if (el) el.style.transition = ""; }, 800);
        }, index * 55);
        obs.unobserve(el);
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [index]);
  return ref;
}

// ─── NAVBAR ───────────────────────────────────────────────────────────────────
function Navbar({ T, isDark, toggleTheme, active, setActive }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", fn, { passive:true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);

  const isVisible = scrolled || menuOpen;

  return (
    <>
      <nav style={{
        position:"fixed", top:0, left:0, right:0, zIndex:100,
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding: isMobile ? "0 20px" : "0 52px", height:68,
        background: isVisible ? T.navBg : "transparent",
        backdropFilter: isVisible ? "blur(20px)" : "none",
        WebkitBackdropFilter: isVisible ? "blur(20px)" : "none",
        borderBottom: isVisible ? `1px solid ${T.border}` : "1px solid transparent",
        transition:"background 0.4s ease, border-color 0.4s ease",
      }}>
        {/* Logo */}
        <span style={{
          fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:18, fontWeight:800, color:T.primary,
          transition:"color 0.3s",
          letterSpacing:"-0.03em",
        }}>
          T. Donnaës
        </span>

        {isMobile ? (
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <ThemeToggle isDark={isDark} toggle={toggleTheme} T={T} />
            <button onClick={() => setMenuOpen(o => !o)} style={{
              width:40, height:40, display:"flex", flexDirection:"column",
              alignItems:"center", justifyContent:"center", gap:5,
              background:"transparent", border:`1px solid ${T.faint}`,
              borderRadius:8, cursor:"pointer", padding:0, outline:"none",
            }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  width:18, height:2, borderRadius:1, background:T.muted,
                  transition:"all 0.3s ease",
                  transform: menuOpen
                    ? i===0 ? "translateY(7px) rotate(45deg)"
                    : i===2 ? "translateY(-7px) rotate(-45deg)" : "scaleX(0)"
                    : "none",
                  opacity: menuOpen && i===1 ? 0 : 1,
                }} />
              ))}
            </button>
          </div>
        ) : (
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {NAV_LINKS.map((link, i) => {
              const on = active === link;
              return (
                <a key={link} href={`#${link.toLowerCase()}`}
                  onClick={e => { e.preventDefault(); setActive(link); smoothScrollTo(link.toLowerCase()); }}
                  style={{
                    fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:13, fontWeight:on?700:500,
                    padding:"7px 18px", borderRadius:8, textDecoration:"none", cursor:"pointer",
                    color: on ? (isDark?"#F0EBF8":"#FAFAFF") : T.muted,
                    background: on ? T.primary : "transparent",
                    border: on ? "none" : `1px solid ${T.faint}`,
                    transition:"all 0.22s ease",
                    animation: `fadeSlideIn 0.5s ${i*0.07}s ease both`,
                    letterSpacing:"0.01em",
                  }}>
                  {link}
                </a>
              );
            })}
            <ThemeToggle isDark={isDark} toggle={toggleTheme} T={T} />
          </div>
        )}
      </nav>

      {/* Mobile menu */}
      {isMobile && (
        <div style={{
          position:"fixed", top:68, left:0, right:0, zIndex:99,
          background:T.navBg, backdropFilter:"blur(20px)",
          borderBottom:`1px solid ${T.border}`,
          display:"flex", flexDirection:"column",
          padding: menuOpen ? "16px 20px 24px" : "0 20px",
          gap:8, maxHeight:menuOpen?320:0, overflow:"hidden",
          transition:"max-height 0.35s cubic-bezier(.4,0,.2,1), padding 0.35s ease",
        }}>
          {NAV_LINKS.map(link => {
            const on = active === link;
            return (
              <a key={link} href={`#${link.toLowerCase()}`}
                onClick={e => { e.preventDefault(); setActive(link); smoothScrollTo(link.toLowerCase()); setMenuOpen(false); }}
                style={{
                  fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:15, fontWeight:on?700:500,
                  padding:"12px 16px", borderRadius:10, textDecoration:"none",
                  color: on ? (isDark?"#F0EEF8":"#FAF8FF") : T.muted,
                  background: on ? T.primary : "transparent",
                  border: on ? "none" : `1px solid ${T.faint}`,
                  transition:"all 0.22s ease",
                }}>
                {link}
              </a>
            );
          })}
        </div>
      )}
    </>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function Hero({ T, isDark }) {
  const isMobile = useIsMobile();
  const reduced  = useReducedMotion();
  const [visible, setVisible] = useState(false);
  const [typed, setTyped]     = useState("");

  const fullText = "Développeur Web";

  // Typewriter effect sur le label
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      setTyped(fullText.slice(0, i + 1));
      i++;
      if (i >= fullText.length) clearInterval(id);
    }, reduced ? 0 : 60);
    return () => clearInterval(id);
  }, [reduced]);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);


  const stats = [
    { val:"20+", label:"Projets réalisés", accent:T.primary   },
    { val:"5",   label:"Ans d'expérience", accent:T.secondary },
    { val:"∞",   label:"Curiosité",        accent:T.tertiary  },
  ];

  const anim = (delay) => reduced ? {} : { animation:`heroFadeUp 0.8s ${delay}s ease both` };

  return (
    <section id="about" style={{
      minHeight:"100vh", position:"relative", overflow:"hidden",
      display:"flex", flexDirection:"column", justifyContent:"center",
      padding: isMobile ? "110px 24px 80px" : "0 0 0 0",
    }}>
      {/* Ligne haute */}
      <div style={{
        position:"absolute", top:isMobile?88:"18%", left:0, right:0, height:1,
        background:`linear-gradient(to right, transparent, ${T.border} 20%, ${T.border} 80%, transparent)`,
        transformOrigin:"left",
        animation: visible && !reduced ? "heroLineIn 1s 0.1s cubic-bezier(.16,1,.3,1) both" : "none",
        zIndex:1,
      }} />

      <div style={{
        position:"relative", zIndex:2,
        padding: isMobile ? "0" : "0 6vw",
        display:"flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        gap: isMobile ? 0 : "4vw",
      }}>

        {/* ══ COLONNE GAUCHE ══ */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>

          {/* Status pill avec glow pulsant */}
          <div style={{
            display:"inline-flex", alignItems:"center", gap:8, alignSelf:"flex-start",
            marginBottom: isMobile?20:24,
            padding:"5px 14px 5px 10px", borderRadius:999,
            border:`1px solid ${T.primary}40`, background:`${T.primary}0C`,
            ...anim(0.15),
          }}>
            <span style={{
              width:6, height:6, borderRadius:"50%", background:T.primary, display:"block",
              boxShadow:`0 0 10px ${T.primary}, 0 0 20px ${T.primary}88`,
              animation:"glowPulse 2.5s ease-in-out infinite",
              color:T.primary,
            }} />
            <span style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:10, color:T.primary, letterSpacing:2.5, textTransform:"uppercase" }}>
              Open to work · Alternance
            </span>
          </div>

          {/* Typewriter label */}
          <div style={{ ...anim(0.22) }}>
            <div style={{
              fontFamily:"'JetBrains Mono', monospace",
              fontSize:isMobile?12:13, color:T.muted, letterSpacing:3.5, textTransform:"uppercase",
              marginBottom:isMobile?4:6,
            }}>
              {typed}<span style={{ borderRight:`2px solid ${T.primary}`, marginLeft:1, animation:"heroFadeIn 0.8s step-end infinite" }}> </span>
            </div>
          </div>

          {/* Nom — char-by-char reveal */}
          <div style={{ lineHeight:0.86 }}>
            <h1 style={{ margin:0, fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:isMobile?"clamp(66px,18vw,100px)":"clamp(84px,9.5vw,148px)", fontWeight:800, lineHeight:0.86, letterSpacing:"-0.04em", color:T.text }}>
              {reduced
                ? "Timéo"
                : <SplitText text="Timéo" delay={0.18} stagger={0.07} />
              }
            </h1>
            <h1 style={{ margin:0, fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:isMobile?"clamp(66px,18vw,100px)":"clamp(84px,9.5vw,148px)", fontWeight:800, lineHeight:0.86, letterSpacing:"-0.04em", color:"transparent", WebkitTextStroke:isMobile?`1.5px ${T.primary}`:`2.5px ${T.primary}`, marginLeft:"8vw" }}>
              {reduced
                ? "Donnaës"
                : <SplitText text="Donnaës" delay={0.52} stagger={0.065} />
              }
            </h1>
          </div>

          {/* Description */}
          <div style={{
            display:"flex", alignItems:"flex-start", gap:isMobile?0:20,
            marginTop:isMobile?28:36, ...anim(0.38),
          }}>
            {!isMobile && <div style={{ width:1, minHeight:80, background:T.border, flexShrink:0, marginTop:4 }} />}
            <p style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontWeight:300, fontSize:isMobile?14:15, lineHeight:1.8, color:T.muted, margin:0, maxWidth:420, letterSpacing:"-0.005em" }}>
              Je construis des interfaces web rapides et élégantes. Passionné par le design système, les animations et les architectures front modernes.
            </p>
          </div>

          {/* CTAs */}
          <div style={{ display:"flex", gap:12, marginTop:isMobile?24:36, flexWrap:"wrap", alignItems:"center", ...anim(0.48) }}>
            <MagneticWrapper>
              <CTAButton href="#projects" T={T} variant="primary" onClick={() => smoothScrollTo("projects")}>
                Projets <Arrow />
              </CTAButton>
            </MagneticWrapper>
            <MagneticWrapper>
              <CTAButton href="#contact" T={T} variant="outline" onClick={() => smoothScrollTo("contact")}>
                Contact <span style={{ opacity:0.5 }}>↗</span>
              </CTAButton>
            </MagneticWrapper>
          </div>
        </div>

        {/* ══ COLONNE DROITE — STATS ══ */}
        {!isMobile && (
          <div style={{ display:"flex", flexDirection:"column", gap:0, flexShrink:0, width:220, ...anim(0.55) }}>
            {stats.map(({ val, label, accent }, i) => (
              <div key={label} style={{
                padding:"28px 0 28px 32px",
                borderLeft:`1px solid ${T.border}`,
                borderTop: i>0 ? `1px solid ${T.border}` : "none",
                position:"relative",
              }}>
                <div style={{ position:"absolute", left:0, top:i===0?0:-1, width:3, height:"calc(100% + 1px)",
                  background:`linear-gradient(to bottom, transparent, ${accent}88, transparent)`,
                }} />
                <AnimCounter target={val} size={40} accent={accent} />
                <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, color:T.muted, letterSpacing:1.5, textTransform:"uppercase", lineHeight:1.4 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Stats mobile */}
        {isMobile && (
          <div style={{ display:"flex", marginTop:40, borderTop:`1px solid ${T.border}`, width:"100%", ...anim(0.6) }}>
            {stats.map(({ val, label, accent }, i) => (
              <div key={label} style={{ flex:1, padding:"20px 0", borderRight:i<stats.length-1?`1px solid ${T.border}`:"none", paddingLeft:i===0?0:16, paddingRight:i<stats.length-1?16:0 }}>
                <AnimCounter target={val} size={28} accent={accent} />
                <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:10, color:T.muted, letterSpacing:1.5, textTransform:"uppercase" }}>{label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ligne basse */}
      <div style={{
        position:"absolute", bottom:isMobile?60:"14%", left:0, right:0, height:1,
        background:`linear-gradient(to right, transparent, ${T.border} 20%, ${T.border} 80%, transparent)`,
        transformOrigin:"right",
        animation: visible&&!reduced ? "heroLineIn 1s 0.3s cubic-bezier(.16,1,.3,1) both" : "none",
        zIndex:1,
      }} />

      {/* Scroll hint animé */}
      <div style={{
        position:"absolute", bottom:28, left:isMobile?24:"6vw",
        display:"flex", alignItems:"center", gap:10, zIndex:2,
        animation: visible&&!reduced ? "heroFadeIn 1s 1.2s ease both" : "none",
      }}>
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width:16, height:1, background:T.faint, borderRadius:1, opacity:1-i*0.3 }} />
          ))}
        </div>
        <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:9, letterSpacing:3, textTransform:"uppercase", color:T.faint }}>
          scroll
        </span>
      </div>

      {/* Année fantôme */}
      <div style={{
        position:"absolute", right:isMobile?"-5%":"-2%", top:"50%", transform:"translateY(-54%)",
        fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:isMobile?"38vw":"28vw", fontWeight:900, lineHeight:1,
        color:"transparent", WebkitTextStroke:`1px ${T.primary}10`,
        userSelect:"none", pointerEvents:"none", zIndex:0, letterSpacing:"-0.04em",
        opacity:visible?1:0, transition:"opacity 1.4s 0.2s ease",
      }}>2026</div>
    </section>
  );
}

// ─── CTA BUTTON ───────────────────────────────────────────────────────────────
function CTAButton({ href, T, variant, onClick, children }) {
  const [hov, setHov] = useState(false);
  const isPrimary = variant === "primary";

  return (
    <a href={href} onClick={e => { e.preventDefault(); onClick?.(); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:11, letterSpacing:2.5, fontWeight:600,
        textTransform:"uppercase", padding:"14px 36px",
        background: isPrimary
          ? `linear-gradient(135deg, ${T.primary}, ${T.secondary}, ${T.tertiary}, ${T.primary})`
          : "transparent",
        backgroundSize: isPrimary ? "300% 300%" : "auto",
        animation: isPrimary ? "gradShift 4s ease infinite" : "none",
        color: !isPrimary ? (hov ? T.text : T.muted) : "#fff",
        border: isPrimary ? "none" : `1px solid ${T.faint}`,
        textDecoration:"none", cursor:"pointer", position:"relative", overflow:"hidden",
        display:"inline-flex", alignItems:"center", gap:8,
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        transition:"transform 0.22s ease, box-shadow 0.3s ease, color 0.3s ease, border-color 0.3s ease, background 0.2s ease",
        boxShadow: isPrimary
          ? hov ? `0 8px 32px ${T.primary}44` : `0 4px 16px ${T.primary}28`
          : "none",
        borderColor: !isPrimary && hov ? `${T.primary}66` : T.faint,
        borderRadius:2,
      }}>
      {/* Shimmer on primary */}
      {isPrimary && (
        <span style={{
          position:"absolute", top:0, width:"40%", height:"100%",
          background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
          transform:"skewX(-15deg)", pointerEvents:"none",
          left: hov ? "120%" : "-60%",
          transition:"left 0.55s ease",
        }} />
      )}
      {/* Fill on outline */}
      {!isPrimary && (
        <span style={{
          position:"absolute", inset:0, background:`${T.primary}0E`,
          transformOrigin:"left", transform: hov ? "scaleX(1)" : "scaleX(0)",
          transition:"transform 0.3s cubic-bezier(.16,1,.3,1)", pointerEvents:"none",
        }} />
      )}
      <span style={{ position:"relative" }}>{children}</span>
    </a>
  );
}

function Arrow() {
  return (
    <svg width={13} height={13} viewBox="0 0 13 13" fill="none" style={{ flexShrink:0, position:"relative" }}>
      <path d="M2 11L11 2M11 2H5M11 2V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── PROJECT CARD ─────────────────────────────────────────────────────────────
function ProjectCard({ T, p, highlighted, index }) {
  const [hov, setHov] = useState(false);
  const ref = useRef(null);
  const reduced = useReducedMotion();

  const onMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 14;
    const y = ((e.clientY - r.top)  / r.height - 0.5) * -14;
    ref.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${y}deg) translateY(-6px) scale(1.015)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(800px) rotateY(0) rotateX(0) translateY(0) scale(1)";
    setHov(false);
  };

  return (
    <div ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={onLeave}
      onMouseMove={onMove}
      style={{
        background: T.bgCard, border:`1px solid ${highlighted ? p.color : hov ? `${p.color}55` : T.border}`,
        borderRadius:12, padding:28, height:"100%", boxSizing:"border-box",
        display:"flex", flexDirection:"column",
        transition:"border-color 0.3s ease, box-shadow 0.3s ease",
        boxShadow: highlighted
          ? `0 0 0 2px ${p.color}, 0 0 32px ${p.color}55`
          : hov ? `0 10px 28px rgba(0,0,0,0.3), 0 0 0 1px ${p.color}44, 0 0 22px ${p.color}30`
          : T.cardShadow,
        willChange:"transform",
        animation: highlighted ? "highlightPulse 2.8s ease forwards" : "none",
      }}>

      {/* Numéro discret */}
      <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:10, color:T.faint, marginBottom:12, letterSpacing:2 }}>
        {String(index+1).padStart(2,"0")}
      </div>

      <h3 style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:19, fontWeight:700, color:T.text, margin:"0 0 6px", transition:"color 0.3s", letterSpacing:"-0.02em" }}>
        {p.title}
      </h3>
      <p style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:9, color:p.color, margin:"0 0 14px", letterSpacing:1.5, textTransform:"uppercase" }}>
        {p.tag}
      </p>
      <p style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontWeight:300, fontSize:13, color:T.muted, lineHeight:1.7, margin:"0 0 18px", flex:1 }}>
        {p.desc}
      </p>

      {/* Ligne séparatrice */}
      <div style={{ height:1, background:`${p.color}20`, margin:"0 0 14px", transition:"background 0.3s" }} />

      {/* Tech tags */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {p.tech.map(t => (
          <span key={t} style={{
            fontFamily:"'JetBrains Mono', monospace", fontSize:9, letterSpacing:1.5,
            padding:"3px 9px", borderRadius:4,
            background:`${p.color}12`, color:p.color, border:`1px solid ${p.color}33`,
            textTransform:"uppercase",
            transition:"background 0.2s",
          }}>{t}</span>
        ))}
      </div>
    </div>
  );
}

// ─── PROJECTS ─────────────────────────────────────────────────────────────────
function useCarouselCols() {
  const [cols, setCols] = useState(3);
  useEffect(() => {
    const fn = () => { const w=window.innerWidth; setCols(w<640?1:w<1024?2:3); };
    fn(); window.addEventListener("resize",fn);
    return () => window.removeEventListener("resize",fn);
  }, []);
  return cols;
}

function Projects({ T, highlightProject }) {
  const cols     = useCarouselCols();
  const isMobile = useIsMobile();
  const reduced  = useReducedMotion();
  const headerRef = useScrollReveal();

  const { slides, totalSlides, paddedProjects } = useMemo(() => {
    const projects = ALL_PROJECTS.map(p => ({ ...p, color:T[p.tKey] }));
    const padded = [...projects];
    const perSlide = isMobile ? 1 : cols*2;
    while (!isMobile && padded.length % perSlide !== 0) padded.push(null);
    const total = Math.ceil(padded.length / perSlide);
    const real  = Array.from({ length:total }, (_,i) => padded.slice(i*perSlide,(i+1)*perSlide));
    return { slides:[real[total-1], ...real, real[0]], totalSlides:total, paddedProjects:padded };
  }, [cols, T, isMobile]);

  const [current,  setCurrent]  = useState(1);
  const [animate,  setAnimate]  = useState(true);
  const [paused,   setPaused]   = useState(false);
  const isSnapping = useRef(false);
  const touchStartX = useRef(null);

  const goTo = useCallback((idx) => {
    if (isSnapping.current) return;
    setAnimate(true); setCurrent(Math.max(0, Math.min(idx, slides.length-1)));
  }, [slides.length]);

  const prev = useCallback(() => goTo(current-1), [current,goTo]);
  const next = useCallback(() => goTo(current+1), [current,goTo]);

  const handleTransitionEnd = useCallback(() => {
    if (current===0) {
      isSnapping.current=true; setAnimate(false); setCurrent(totalSlides);
      requestAnimationFrame(() => requestAnimationFrame(() => { isSnapping.current=false; setAnimate(true); }));
    } else if (current===slides.length-1) {
      isSnapping.current=true; setAnimate(false); setCurrent(1);
      requestAnimationFrame(() => requestAnimationFrame(() => { isSnapping.current=false; setAnimate(true); }));
    }
  }, [current, totalSlides, slides.length]);

  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => { if (!isSnapping.current) next(); }, 5000);
    return () => clearTimeout(id);
  }, [current, paused, next]);

  useEffect(() => {
    isSnapping.current=false; setAnimate(false); setCurrent(1);
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
  }, [cols, isMobile]);

  useEffect(() => {
    if (!highlightProject) return;
    const idx = paddedProjects.findIndex(p => p?.title===highlightProject);
    if (idx===-1) return;
    goTo(Math.floor(idx/(isMobile?1:cols*2))+1);
  }, [highlightProject]); // eslint-disable-line

  const realCurrent = current===0 ? totalSlides-1 : current>=slides.length-1 ? 0 : current-1;
  const px = isMobile?"20px":"52px";

  return (
    <section id="projects" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"center", padding:isMobile?"80px 0":"100px 0", maxWidth:1100, margin:"0 auto", width:"100%" }}>
      <div style={{ padding:`0 ${px}` }}>
        <SectionHeader T={T} overline="Réalisations" title="Projets" animRef={headerRef} num="01" />
      </div>

      <div style={{ position:"relative", padding:`0 ${isMobile?"20px":px}` }}
        onMouseEnter={() => !isMobile && setPaused(true)}
        onMouseLeave={() => !isMobile && setPaused(false)}
      >
        {/* Arrow gauche */}
        {!isMobile && <NavArrow dir="left" T={T} onClick={prev} />}

        <div style={{ overflow:"hidden", borderRadius:4, padding:"40px 0", margin:"-32px 0 -32px" }}
          onTouchStart={e => { touchStartX.current=e.touches[0].clientX; setPaused(true); }}
          onTouchEnd={e => {
            if (touchStartX.current===null) return;
            const dx=e.changedTouches[0].clientX-touchStartX.current;
            if (Math.abs(dx)>40) dx<0?next():prev();
            touchStartX.current=null;
            setTimeout(()=>setPaused(false),2000);
          }}
        >
          <div onTransitionEnd={handleTransitionEnd} style={{
            display:"flex",
            transform:`translateX(-${current*100}%)`,
            transition: animate&&!reduced ? "transform 0.6s cubic-bezier(.4,0,.2,1)" : "none",
          }}>
            {slides.map((slide, si) => (
              <div key={si} style={{
                minWidth:"100%", boxSizing:"border-box",
                padding: isMobile ? "0" : "0 28px",
                ...(isMobile
                  ? { display:"flex", flexDirection:"column" }
                  : { display:"grid", gridTemplateColumns:`repeat(${cols},1fr)`, gridAutoRows:"1fr", gap:24, alignItems:"stretch" }
                ),
              }}>
                {slide.filter(Boolean).map((p,ci) => p && (
                  <ProjectCard key={p.title} T={T} p={p}
                    highlighted={highlightProject===p.title}
                    index={ALL_PROJECTS.findIndex(x=>x.title===p.title)}
                  />
                ))}
                {!isMobile && slide.filter(x=>!x).map((_,ci)=>(
                  <div key={`empty-${ci}`} style={{ visibility:"hidden" }} />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Arrow droite */}
        {!isMobile && <NavArrow dir="right" T={T} onClick={next} />}
      </div>

      {/* Pagination */}
      {isMobile ? (
        <MobilePagination T={T} total={totalSlides} current={realCurrent} goTo={goTo} prev={prev} next={next} />
      ) : (
        <DesktopPagination T={T} total={totalSlides} current={realCurrent} goTo={goTo} paused={paused} px={px} />
      )}
    </section>
  );
}

function NavArrow({ dir, T, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position:"absolute", [dir==="left"?"left":"right"]:8, top:"50%", transform:"translateY(-50%)", zIndex:10,
        width:40, height:40, borderRadius:"50%",
        border:`1px solid ${hov?T.primary:T.border}`,
        background:T.bgCard, cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
        color:hov?T.primary:T.muted, fontSize:18,
        transition:"all 0.2s ease", outline:"none",
        boxShadow:hov?`0 0 16px ${T.primary}44`:"none",
      }}>
      {dir==="left"?"‹":"›"}
    </button>
  );
}

function MobilePagination({ T, total, current, goTo, prev, next }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", margin:"16px 20px 0" }}>
      <PagBtn T={T} onClick={prev}>‹</PagBtn>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
        <div style={{ display:"flex", gap:6 }}>
          {Array.from({length:total}).map((_,i)=>(
            <button key={i} onClick={()=>goTo(i+1)} style={{
              width:i===current?20:6, height:6, borderRadius:4, border:"none", cursor:"pointer", padding:0,
              background:i===current?T.primary:T.faint, transition:"all 0.3s ease", outline:"none",
              boxShadow:i===current?`0 0 8px ${T.primary}88`:"none",
            }} />
          ))}
        </div>
        <span style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:11, color:T.muted, letterSpacing:1 }}>
          {current+1} / {total}
        </span>
      </div>
      <PagBtn T={T} onClick={next}>›</PagBtn>
    </div>
  );
}

function PagBtn({ T, onClick, children }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ width:44, height:44, borderRadius:10, border:`1px solid ${hov?T.primary:T.border}`, background:T.bgCard, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:hov?T.primary:T.muted, fontSize:22, outline:"none", transition:"all 0.2s ease", flexShrink:0 }}>
      {children}
    </button>
  );
}

function DesktopPagination({ T, total, current, goTo, paused, px }) {
  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:28, padding:`0 ${px}` }}>
        {Array.from({length:total}).map((_,i)=>(
          <button key={i} onClick={()=>goTo(i+1)} style={{
            width:i===current?24:8, height:8, borderRadius:4, border:"none", cursor:"pointer", padding:0,
            background:i===current?T.primary:T.faint, transition:"all 0.3s ease", outline:"none",
            boxShadow:i===current?`0 0 10px ${T.primary}88`:"none",
          }} />
        ))}
      </div>
      <div style={{ margin:`16px ${px} 0`, height:2, borderRadius:2, background:T.trackBg, overflow:"hidden" }}>
        {!paused && (
          <div key={`${current}-${paused}`} style={{ height:"100%", borderRadius:2, background:T.primary, animation:"carouselProgress 5s linear forwards" }} />
        )}
      </div>
    </>
  );
}

// ─── SKILLS ───────────────────────────────────────────────────────────────────
function Skills({ T, onSkillClick }) {
  const isMobile = useIsMobile();
  const reduced  = useReducedMotion();
  const headerRef = useScrollReveal();
  const skillMap = Object.fromEntries(SKILLS_DATA.map(s => [s.name, { ...s, color:T[s.tKey] }]));

  return (
    <section id="skills" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"center", padding:isMobile?"80px 20px":"100px 52px", maxWidth:1100, margin:"0 auto", width:"100%", boxSizing:"border-box" }}>
      <SectionHeader T={T} overline="Stack technique" title="Skills" animRef={headerRef} num="02" />

      <div style={{ display:"flex", flexDirection:isMobile?"column":"row", gap:isMobile?32:40 }}>
        {SKILL_GROUPS.map((group, gi) => (
          <div key={group.label} style={{ flex:1 }}>
            <div style={{
              fontFamily:"'JetBrains Mono', monospace", fontSize:10,
              color:T.muted, letterSpacing:3, textTransform:"uppercase",
              marginBottom:16, paddingBottom:12,
              borderBottom:`1px solid ${T.border}`,
              display:"flex", alignItems:"center", gap:8,
            }}>
              <span style={{ display:"inline-block", width:20, height:1, background:T.border }} />
              {group.label}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:10 }}>
              {group.skills.map((name, i) => {
                const s = skillMap[name]; if (!s) return null;
                const tags    = SKILL_TO_TECH[s.name] || [s.name];
                const matches = ALL_PROJECTS.filter(p => p.tech.some(t => tags.some(tag => t.toLowerCase().includes(tag.toLowerCase()))));
                return (
                  <SkillCard key={s.name} s={s} T={T} hasMatch={matches.length>0}
                    matchCount={matches.length} index={gi * 4 + i} reduced={reduced}
                    onClick={() => {
                      if (!matches.length) return;
                      onSkillClick(matches[Math.floor(Math.random()*matches.length)].title);
                    }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SkillCard({ s, T, hasMatch, matchCount, index, reduced, onClick }) {
  const ref  = useStaggerReveal(index);
  const [hov, setHov] = useState(false);
  const icon = TECH_ICONS[s.name];

  return (
    <div ref={ref} onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding:"20px 20px 18px", borderRadius:10, cursor:hasMatch?"pointer":"default",
        border:`1px solid ${hov?`${s.color}66`:s.color+"22"}`,
        background:hov?`${s.color}16`:`${s.color}07`,
        display:"flex", flexDirection:"column", gap:14,
        transition:"border-color 0.25s ease, background 0.25s ease, box-shadow 0.3s ease, transform 0.25s ease",
        transform:hov&&!reduced?"translateY(-5px) scale(1.02)":"translateY(0) scale(1)",
        boxShadow:hov&&hasMatch?`0 16px 40px ${s.color}30, 0 0 0 1px ${s.color}33`:"none",
        position:"relative", overflow:"hidden",
      }}>

      {/* Glow interne */}
      <span style={{
        position:"absolute", inset:0,
        background:`radial-gradient(circle at 40% 60%, ${s.color}18, transparent 65%)`,
        opacity:hov?1:0, transition:"opacity 0.4s ease", pointerEvents:"none",
      }} />

      {/* Icône + badge projet */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", position:"relative" }}>
        <span style={{ opacity:hov?1:0.65, transition:"opacity 0.25s, transform 0.3s", transform:hov&&!reduced?"scale(1.1) rotate(-4deg)":"scale(1) rotate(0deg)" }}>
          {icon && icon(s.color)}
        </span>
        {hasMatch && (
          <span style={{
            fontFamily:"'JetBrains Mono', monospace", fontSize:8, color:s.color,
            background:`${s.color}18`, border:`1px solid ${s.color}33`,
            padding:"2px 6px", borderRadius:4, letterSpacing:0.5,
            opacity:hov?1:0.6, transition:"opacity 0.25s", position:"relative",
          }}>
            ↗ {matchCount}
          </span>
        )}
      </div>

      {/* Nom */}
      <span style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:14, fontWeight:600, color:T.text, position:"relative", letterSpacing:"-0.01em" }}>
        {s.name}
      </span>
    </div>
  );
}

// ─── CONTACT ──────────────────────────────────────────────────────────────────
function Contact({ T }) {
  const [form,   setForm]   = useState({ name:"", email:"", message:"" });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");
  const isMobile = useIsMobile();
  const headerRef = useScrollReveal();
  const formRef   = useScrollReveal();

  const inputStyle = {
    width:"100%", padding:"13px 16px", borderRadius:6,
    border:`1px solid ${T.border}`, background:T.inputBg,
    color:T.text, fontSize:14, fontFamily:"'Bricolage Grotesque', sans-serif",
    fontWeight:400, letterSpacing:"-0.005em",
    outline:"none", boxSizing:"border-box",
    transition:"border-color 0.2s, background 0.3s, box-shadow 0.2s",
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setStatus("sending"); setErrMsg("");
    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, { from_name:form.name, from_email:form.email, message:form.message, to_name:"Timéo" }, EMAILJS_PUBLIC_KEY);
      setStatus("sent"); setForm({ name:"", email:"", message:"" });
    } catch(err) {
      setErrMsg("Une erreur est survenue. Réessaie ou contacte-moi directement."); setStatus("error");
    }
  };

  return (
    <section id="contact" style={{ minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"center", padding:isMobile?"80px 20px 100px":"100px 52px 140px", maxWidth:1100, margin:"0 auto", width:"100%", boxSizing:"border-box" }}>
      <div style={{ maxWidth:640 }}>
        <SectionHeader T={T} overline="Envie de me contacter ?" title="Contact" animRef={headerRef} num="03" />

        {status==="sent" ? (
          <GlassCard T={T} style={{ padding:isMobile?32:52, textAlign:"center", animation:"scaleIn 0.5s ease both" }}>
            <div style={{ width:56, height:56, borderRadius:"50%", background:`${T.primary}16`, border:`1px solid ${T.primary}44`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:22, color:T.primary, boxShadow:`0 0 20px ${T.primary}33` }}>✦</div>
            <h3 style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:22, color:T.primary, margin:"0 0 10px" }}>Message envoyé !</h3>
            <p style={{ fontFamily:"'Bricolage Grotesque', sans-serif", color:T.muted, margin:"0 0 28px", fontStyle:"italic" }}>Je te répondrai dans les plus brefs délais.</p>
            <button onClick={()=>setStatus("idle")} style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:13, color:T.muted, background:"transparent", border:`1px solid ${T.border}`, borderRadius:6, padding:"8px 18px", cursor:"pointer", transition:"all 0.2s" }}
              onMouseEnter={e=>{e.target.style.borderColor=T.primary;e.target.style.color=T.primary;}}
              onMouseLeave={e=>{e.target.style.borderColor=T.border;e.target.style.color=T.muted;}}>
              Envoyer un autre message
            </button>
          </GlassCard>
        ) : (
          <div ref={formRef} style={{ opacity:0, transform:"translateY(24px)", transition:"opacity 0.7s ease, transform 0.7s ease" }}>
            <GlassCard T={T} style={{ padding:isMobile?"32px 24px":"44px 48px" }}>
              <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:22 }}>
                {[{key:"name",label:"Nom",type:"text",placeholder:"Votre nom"},{key:"email",label:"Email",type:"email",placeholder:"votre.mail@gmail.com"}].map(({key,label,type,placeholder})=>(
                  <FloatingLabel key={key} label={label} T={T}>
                    <input type={type} placeholder={placeholder} value={form[key]}
                      onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} required
                      style={inputStyle}
                      onFocus={e=>{e.target.style.borderColor=`${T.primary}66`;e.target.style.boxShadow=`0 0 0 3px ${T.primary}18`;}}
                      onBlur={e=>{e.target.style.borderColor=T.border;e.target.style.boxShadow="none";}}
                    />
                  </FloatingLabel>
                ))}
                <FloatingLabel label="Message" T={T}>
                  <textarea placeholder="De quoi souhaitez-vous parler..." value={form.message}
                    onChange={e=>setForm(f=>({...f,message:e.target.value}))}
                    required rows={5} style={{...inputStyle,resize:"vertical"}}
                    onFocus={e=>{e.target.style.borderColor=`${T.primary}66`;e.target.style.boxShadow=`0 0 0 3px ${T.primary}18`;}}
                    onBlur={e=>{e.target.style.borderColor=T.border;e.target.style.boxShadow="none";}}
                  />
                </FloatingLabel>

                {status==="error" && (
                  <p style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:13, fontStyle:"italic", color:"#FF2D78", margin:0, padding:"10px 14px", background:"rgba(255,45,120,0.06)", borderRadius:6, border:"1px solid rgba(255,45,120,0.18)" }}>
                    ⚠ {errMsg}
                  </p>
                )}

                <SubmitButton T={T} isMobile={isMobile} status={status} />
              </form>
            </GlassCard>
          </div>
        )}
      </div>
    </section>
  );
}

function FloatingLabel({ label, T, children }) {
  return (
    <div>
      <label style={{ fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:10, color:T.faint, letterSpacing:2, textTransform:"uppercase", display:"block", marginBottom:8, transition:"color 0.3s" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function SubmitButton({ T, isMobile, status }) {
  const [hov, setHov] = useState(false);
  const sending = status==="sending";
  return (
    <button type="submit" disabled={sending}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        fontFamily:"'Bricolage Grotesque', sans-serif", fontSize:14, fontWeight:700,
        padding:"14px 30px", borderRadius:8, border:"none",
        cursor:sending?"not-allowed":"pointer",
        background:sending
          ? `linear-gradient(135deg, ${T.primary}88, ${T.secondary}88)`
          : `linear-gradient(135deg, ${T.primary}, ${T.secondary})`,
        color:"#fff",
        boxShadow:hov&&!sending?`0 0 0 1px ${T.primary}88, 0 8px 30px ${T.primary}55`:`0 4px 16px ${T.primary}28`,
        transition:"all 0.2s ease",
        transform:hov&&!sending?"translateY(-2px) scale(1.02)":"translateY(0) scale(1)",
        opacity:sending?0.8:1,
        alignSelf:isMobile?"stretch":"flex-start", textAlign:"center",
        display:"flex", alignItems:"center", justifyContent:"center", gap:8,
      }}>
      {sending ? (
        <>
          <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,0.35)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />
          Envoi en cours…
        </>
      ) : "Envoyer →"}
    </button>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function Portfolio() {
  const [active,           setActive]           = useState("About");
  const [isDark,           setIsDark]           = useState(true);
  const [highlightProject, setHighlightProject] = useState(null);
  const T = isDark ? THEMES.dark : THEMES.light;

  // Load animation libs + fonts
  useEffect(() => {
    loadLibs();

    // Fonts
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=JetBrains+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);

    // Favicon
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><rect width="200" height="200" rx="36" fill="#07050F"/><polygon points="200,110 200,200 110,200" fill="#FF2D78" opacity="0.35"/><rect x="34" y="54" width="132" height="22" rx="4" fill="#C400D6"/><rect x="78" y="74" width="30" height="82" rx="4" fill="#C400D6"/></svg>`;
    const url = "data:image/svg+xml;base64," + btoa(svg);
    let fav = document.querySelector("link[rel~='icon']");
    if (!fav) { fav=document.createElement("link"); fav.rel="icon"; document.head.appendChild(fav); }
    fav.type="image/svg+xml"; fav.href=url;
  }, []);

  // Active section tracking
  useEffect(() => {
    const ids = NAV_LINKS.map(l => l.toLowerCase());
    const pick = () => {
      if (scrollingProgrammatically) return;
      const vh = window.innerHeight;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= vh * 0.4) current = id;
      }
      setActive(NAV_LINKS.find(l => l.toLowerCase() === current) || "About");
    };
    pick();
    window.addEventListener("scroll", pick, { passive: true });
    return () => window.removeEventListener("scroll", pick);
  }, []);

  const handleSkillClick = useCallback((title) => {
    setHighlightProject(title);
    smoothScrollTo("projects");
    setTimeout(() => setHighlightProject(null), 2800);
  }, []);

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <div className="grain" style={{
        background:T.bg, minHeight:"100vh", color:T.text,
        position:"relative", transition:"background 0.4s, color 0.4s", overflowX:"hidden",
      }}>
        {/* Grille de fond */}
        <div style={{
          position:"fixed", inset:0, zIndex:0, pointerEvents:"none",
          backgroundImage:`linear-gradient(${T.gridColor} 1px, transparent 1px), linear-gradient(90deg, ${T.gridColor} 1px, transparent 1px)`,
          backgroundSize:"72px 72px", transition:"background-image 0.4s",
        }} />

        {/* Orbes flottantes */}
        <GradientOrbs T={T} />

        {/* Curseur custom */}
        <CustomCursor color={T.cursorColor} />

        <Navbar T={T} isDark={isDark} toggleTheme={() => setIsDark(d=>!d)} active={active} setActive={setActive} />

        <div style={{ position:"relative", zIndex:1 }}>
          <Hero T={T} isDark={isDark} />
          <MarqueeStrip T={T} speed={26} />
          <Projects T={T} highlightProject={highlightProject} />
          <MarqueeStrip T={T} reversed speed={20} />
          <Skills T={T} onSkillClick={handleSkillClick} />
          <MarqueeStrip T={T} speed={22} />
          <Contact T={T} />
        </div>

        <footer style={{
          textAlign:"center", padding:"36px 0 40px",
          borderTop:`1px solid ${T.border}`,
          fontFamily:"'JetBrains Mono', monospace", fontSize:10,
          color:T.faint, letterSpacing:2.5, position:"relative", zIndex:1,
          textTransform:"uppercase", transition:"all 0.3s",
        }}>
          Timéo Donnaës — {new Date().getFullYear()}
          <span style={{ margin:"0 12px", opacity:0.3 }}>·</span>
          <span style={{ color:T.primary, opacity:0.7 }}>Made with React</span>
        </footer>
      </div>
    </>
  );
}
